import {
  LaunchableAnalyticsResponse,
  LaunchableAnalyticsTimeSeries,
} from "server";
import { formatDailyLaunchableMetrics } from "contexts/LaunchableContext";
import agent from "server";

export interface LaunchableChartDataPoint {
  date: string;
  [key: string]: string | number;
}

/**
 * Transforms launchable metrics data into the format expected by chart components
 * @param dailyMetrics - Raw metrics data from API
 * @param selectedValues - Array of selected launchable display names
 * @param selectedMetric - Either "Views" or "Deploys"
 * @param activelaunchables - Array of active launchables from context
 * @returns Transformed data array for chart consumption
 */
export const transformLaunchableMetricsForChart = (
  dailyMetrics: { [launchableId: string]: LaunchableAnalyticsTimeSeries[] },
  selectedValues: string[],
  selectedMetric: string,
  activelaunchables: any[]
): LaunchableChartDataPoint[] => {
  if (
    !dailyMetrics ||
    Object.keys(dailyMetrics).length === 0 ||
    selectedValues[0] === "None"
  ) {
    return [];
  }

  // Get all unique dates from all selected launchables
  const allDates = new Set<string>();
  selectedValues.forEach((selectedKey) => {
    if (selectedKey === "None") return;

    // Find the launchable ID that corresponds to this display key
    const launchableId = Object.keys(dailyMetrics).find((id) => {
      const launchable = activelaunchables.find((l) => l.id === id);
      return (
        launchable &&
        `${launchable.name} (...${launchable.id.slice(-5)})` === selectedKey
      );
    });

    if (launchableId && dailyMetrics[launchableId]) {
      dailyMetrics[launchableId].forEach((dataPoint) => {
        allDates.add(dataPoint.date.split("T")[0]); // Extract YYYY-MM-DD
      });
    }
  });

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort();

  // Transform data into the format expected by AreaChart
  return sortedDates.map((dateKey) => {
    const dataPoint: LaunchableChartDataPoint = {
      date: formatDailyLaunchableMetrics(dateKey),
    };

    // For each selected launchable, add its metric value for this date
    selectedValues.forEach((selectedKey) => {
      if (selectedKey === "None") return;

      // Find the launchable ID that corresponds to this display key
      const launchableId = Object.keys(dailyMetrics).find((id) => {
        const launchable = activelaunchables.find((l) => l.id === id);
        return (
          launchable &&
          `${launchable.name} (...${launchable.id.slice(-5)})` === selectedKey
        );
      });

      if (launchableId && dailyMetrics[launchableId]) {
        const timeSeriesData = dailyMetrics[launchableId];
        const dataForDate = timeSeriesData.find(
          (d) => d.date.split("T")[0] === dateKey
        );

        if (dataForDate) {
          // Use the selected metric (Views or Deploys)
          dataPoint[selectedKey] =
            selectedMetric === "Views"
              ? dataForDate.views
              : dataForDate.deploys;
        } else {
          dataPoint[selectedKey] = 0;
        }
      } else {
        dataPoint[selectedKey] = 0;
      }
    });

    return dataPoint;
  });
};

/**
 * Generates launchable display keys from active launchables
 * @param activelaunchables - Array of active launchables
 * @returns Array of display keys in format "Name (...12345)"
 */
export const generateLaunchableDisplayKeys = (
  activelaunchables: any[]
): string[] => {
  return activelaunchables.map(
    (launchable) => `${launchable.name} (...${launchable.id.slice(-5)})`
  );
};

/**
 * Aggregates all launchables' metrics for each day into a single metric value
 * @param dailyMetrics - Raw metrics data from API
 * @param selectedMetric - Either "Views" or "Deploys"
 * @param activelaunchables - Array of active launchables from context
 * @returns Transformed data array with aggregated metrics per day
 */
export const aggregateLaunchableMetricsByDay = (
  dailyMetrics: { [launchableId: string]: LaunchableAnalyticsTimeSeries[] },
  selectedMetric: string,
  activelaunchables: any[]
): { date: string; [key: string]: string | number }[] => {
  if (!dailyMetrics || Object.keys(dailyMetrics).length === 0) {
    return [];
  }

  // Create a map to aggregate metrics by date
  const dateAggregation = new Map<string, number>();

  // Process all launchables and aggregate their metrics by date
  Object.keys(dailyMetrics).forEach((launchableId) => {
    const launchable = activelaunchables.find((l) => l.id === launchableId);
    if (!launchable || !dailyMetrics[launchableId]) return;

    dailyMetrics[launchableId].forEach((dataPoint) => {
      const dateKey = dataPoint.date.split("T")[0]; // Extract YYYY-MM-DD
      const metricValue =
        selectedMetric === "Views" ? dataPoint.views : dataPoint.deploys;

      if (dateAggregation.has(dateKey)) {
        dateAggregation.set(
          dateKey,
          dateAggregation.get(dateKey)! + metricValue
        );
      } else {
        dateAggregation.set(dateKey, metricValue);
      }
    });
  });

  // Convert map to array and sort by date
  return Array.from(dateAggregation.entries())
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([dateKey, aggregatedValue]) => ({
      date: formatDailyLaunchableMetrics(dateKey),
      [selectedMetric]: aggregatedValue,
    }));
};

// Transformations from AggregateLaunchableMetricsResponse
export interface LaunchableCountsPerUser {
  creator_id: string;
  analytics: LaunchablePerformance;
}

export interface LaunchablePerformance {
  views: number;
  deploys: number;
}

// Types for the devplane response (simplified to match what we need)
export interface AggregateLaunchableMetricsItem {
  grouped_by: Record<string, string>;
  event_data: Record<string, number>;
  date?: string;
}

export interface AggregateLaunchableMetricsResponse {
  items: AggregateLaunchableMetricsItem[];
}

/**
 * TypeScript equivalent of mapAggregateLaunchableMetricsResponseToLaunchableCountsPerUser
 * Maps the devplane response to launchable counts per user
 */
export function mapAggregateLaunchableMetricsResponseToLaunchableCountsPerUser(
  res: AggregateLaunchableMetricsResponse,
  creatorIDs: string[]
): LaunchableCountsPerUser[] {
  // Initialize map with all creator IDs set to zero values
  const creatorMap = new Map<string, LaunchablePerformance>();
  for (const creatorID of creatorIDs) {
    creatorMap.set(creatorID, {
      views: 0,
      deploys: 0,
    });
  }

  // Loop through the items and add up the values
  for (const item of res.items) {
    // Extract creator ID from GroupedBy map
    const creatorID = item.grouped_by["creator_id"];
    if (!creatorID) {
      // Skip items without creator_id
      continue;
    }

    // Only process if this creator is in our list
    const analytics = creatorMap.get(creatorID);
    if (analytics) {
      // Extract views and deployments from EventData
      const views = item.event_data["Launchable Viewed"] || 0;
      const deploys = item.event_data["Launchable Deployed"] || 0;

      // Add to existing values
      analytics.views += views;
      analytics.deploys += deploys;
    }
  }

  // Convert map to array
  const result: LaunchableCountsPerUser[] = [];
  for (const [creatorID, analytics] of creatorMap) {
    result.push({
      creator_id: creatorID,
      analytics: analytics,
    });
  }

  return result;
}

/**
 * TypeScript equivalent of mapAggregateLaunchableMetricsResponseToTimeSeries
 * Maps the devplane response to time series data for each launchable
 */
export function mapAggregateLaunchableMetricsResponseToTimeSeries(
  res: AggregateLaunchableMetricsResponse,
  launchableIDs: string[],
  startDate: Date,
  endDate: Date
): Record<string, LaunchableAnalyticsTimeSeries[]> {
  // First, collect all the actual data points
  const dataMap = new Map<string, Map<string, LaunchableAnalyticsTimeSeries>>();
  if (res.items && res.items.length > 0) {
    for (const item of res.items) {
      // Parse the date string to Date
      if (!item.date) {
        continue;
      }

      const date = new Date(item.date);
      if (isNaN(date.getTime())) {
        // If parsing fails, skip this item
        continue;
      }

      // Extract launchable ID from GroupedBy map
      const launchableID = item.grouped_by["launchable_id"];
      if (!launchableID) {
        // Skip items without launchable_id
        continue;
      }

      // Extract views and deployments from EventData
      const views = item.event_data["Launchable Viewed"] || 0;
      const deploys = item.event_data["Launchable Deployed"] || 0;

      // Initialize the launchable map if it doesn't exist
      if (!dataMap.has(launchableID)) {
        dataMap.set(launchableID, new Map());
      }

      const dateKey = formatDateKey(date);
      dataMap.get(launchableID)!.set(dateKey, {
        date: date.toISOString(),
        views: views,
        deploys: deploys,
      });
    }
  }

  // Now generate complete time series for each launchable
  const timeSeries: Record<string, LaunchableAnalyticsTimeSeries[]> = {};

  for (const launchableID of launchableIDs) {
    const series: LaunchableAnalyticsTimeSeries[] = [];

    // Iterate through each day from start to end
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateKey = formatDateKey(currentDate);

      // Get data for this date or use zero values
      const launchableData = dataMap.get(launchableID);
      if (launchableData && launchableData.has(dateKey)) {
        series.push(launchableData.get(dateKey)!);
      } else {
        // No data for this date, use zero values
        series.push({
          date: currentDate.toISOString(),
          views: 0,
          deploys: 0,
        });
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    timeSeries[launchableID] = series;
  }

  return timeSeries;
}

/**
 * TypeScript equivalent of makeCumulativeTimeSeries
 * Makes values cumulative for each launchable
 */
export function makeCumulativeTimeSeries(
  timeSeries: Record<string, LaunchableAnalyticsTimeSeries[]>,
  launchableIDs: string[]
): Record<string, LaunchableAnalyticsTimeSeries[]> {
  const result: Record<string, LaunchableAnalyticsTimeSeries[]> = {};

  for (const launchableID of launchableIDs) {
    const series = timeSeries[launchableID];
    if (series) {
      const cumulativeSeries: LaunchableAnalyticsTimeSeries[] = [];
      let cumulativeViews = 0;
      let cumulativeDeploys = 0;

      // Process each day in the series (already complete from startDate to endDate)
      for (const point of series) {
        // Add to cumulative totals
        cumulativeViews += point.views;
        cumulativeDeploys += point.deploys;

        // Add to result series with cumulative values
        cumulativeSeries.push({
          date: point.date,
          views: cumulativeViews,
          deploys: cumulativeDeploys,
        });
      }

      result[launchableID] = cumulativeSeries;
    }
  }

  return result;
}

/**
 * TypeScript equivalent of the inline aggregation logic in GetTotalsForLaunchables
 * Aggregates total views and deploys from all items
 */
export function aggregateTotalsForLaunchables(
  res: AggregateLaunchableMetricsResponse
): LaunchablePerformance {
  if (!res.items) {
    return {
      views: 0,
      deploys: 0,
    };
  }
  let totalViews = 0;
  let totalDeploys = 0;

  for (const item of res.items) {
    totalViews += item.event_data["Launchable Viewed"] || 0;
    totalDeploys += item.event_data["Launchable Deployed"] || 0;
  }

  return {
    views: totalViews,
    deploys: totalDeploys,
  };
}

export function aggregateLaunchableTotalsByLaunchableId(
  launchableIDs: string[],
  res: AggregateLaunchableMetricsResponse
): Record<string, LaunchablePerformance> {
  // Initialize map for launchable IDs
  const launchableMap = new Map<string, LaunchablePerformance>();

  // Initialize all provided launchable IDs with zero values
  for (const launchableID of launchableIDs) {
    launchableMap.set(launchableID, {
      views: 0,
      deploys: 0,
    });
  }

  // Loop through response items and aggregate metrics
  for (const item of res.items) {
    // Extract launchable ID from grouped_by
    const launchableID = item.grouped_by["launchable_id"];
    if (!launchableID) {
      continue; // Skip items without launchable_id
    }

    // Get current metrics or initialize if not in our list
    let currentMetrics = launchableMap.get(launchableID);
    if (!currentMetrics) {
      // If this launchable ID wasn't in our input list, still add it
      currentMetrics = { views: 0, deploys: 0 };
      launchableMap.set(launchableID, currentMetrics);
    }

    // Extract and add views and deploys from event_data
    const views = item.event_data["Launchable Viewed"] || 0;
    const deploys = item.event_data["Launchable Deployed"] || 0;

    currentMetrics.views += views;
    currentMetrics.deploys += deploys;
  }

  // Convert Map to Record and return
  const result: Record<string, LaunchablePerformance> = {};
  for (const [launchableID, metrics] of launchableMap) {
    result[launchableID] = metrics;
  }

  return result;
}

export function sortLaunchableTotalsByType(
  launchableTotals: Record<string, LaunchablePerformance>,
  type: "views" | "deploys"
): (LaunchablePerformance & { launchableId: string })[] {
  return Object.entries(launchableTotals)
    .map(([launchableId, performance]) => ({
      launchableId,
      views: performance.views,
      deploys: performance.deploys,
    }))
    .sort((a, b) => b[type] - a[type]);
}

/**
 * Helper function to format date as YYYY-MM-DD string
 * TypeScript equivalent of Go's date.Format("2006-01-02")
 */
function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Helper function to process daily time series with optional cumulative calculation
 * This combines the logic that was in GetDailyTimeSeriesForLaunchables
 */
export function processDailyTimeSeriesForLaunchables(
  res: AggregateLaunchableMetricsResponse,
  launchableIDs: string[],
  startDate: Date,
  endDate: Date,
  isCumulative: boolean
): Record<string, LaunchableAnalyticsTimeSeries[]> {
  let timeSeries = mapAggregateLaunchableMetricsResponseToTimeSeries(
    res,
    launchableIDs,
    startDate,
    endDate
  );

  // If cumulative is requested, process the time series to make cumulative
  if (isCumulative) {
    timeSeries = makeCumulativeTimeSeries(timeSeries, launchableIDs);
  }

  return timeSeries;
}

export async function getDailyTimeSeriesForLaunchables(
  launchableIDs: string[],
  startDate: Date,
  endDate: Date
): Promise<LaunchableAnalyticsResponse> {
  const res = await agent.Launchables.getLaunchableMetricsFromDevPlane({
    event_types: ["Launchable Viewed", "Launchable Deployed"],
    group_by: ["launchable_id"],
    time_granularity: "day",
    launchable_ids: launchableIDs,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  });
  return res;
}

export async function getLaunchableTotalsPerUser(
  creatorIDs: string[]
): Promise<LaunchableAnalyticsResponse> {
  const res = await agent.Launchables.getLaunchableMetricsFromDevPlane({
    event_types: ["Launchable Viewed", "Launchable Deployed"],
    group_by: ["creator_id"],
    time_granularity: "all",
    creator_ids: creatorIDs,
  });
  return res;
}

export async function getLaunchableTotals(
  launchableIDs: string[]
): Promise<LaunchableAnalyticsResponse> {
  const res = await agent.Launchables.getLaunchableMetricsFromDevPlane({
    event_types: ["Launchable Viewed", "Launchable Deployed"],
    group_by: ["launchable_id"],
    time_granularity: "all",
    launchable_ids: launchableIDs,
  });
  return res;
}
